import type { AxiosInstance } from 'axios'
import type { H3Event } from 'h3'
import type { ComputedRef } from 'vue'
import type { NuxtApp } from '#app'
import type { HomePopularNormalized, HomePopularPayload } from '~/utils/homePopular'
import {
  cloneHomePopular,
  DEFAULT_HOME_POPULAR_FALLBACK_FILE,
  EMPTY_HOME_POPULAR,
  mergeHomePopular,
} from '~/utils/homePopular'
import { loadFallbackJsonPayload } from '~/utils/helpers'
import { CachedPageUtils } from '~/utils/CachedPageUtils'

export type UseHomePopularDataReturn = {
  homePopular: ComputedRef<HomePopularNormalized>
}

/**
 * @param {AxiosInstance} api - Client HTTP API.
 * @param {string} fallbackFile - Fichier ou URL fallback (`NUXT_PUBLIC_HOME_POPULAR_FALLBACK_URL`).
 * @param {string} requestOrigin - Origin SSR / client pour le fetch static.
 * @param {H3Event | undefined} requestEvent - Requête en cours côté serveur, absente dans le navigateur.
 * @returns {Promise<HomePopularNormalized>} Sections fusionnées JSON + API.
 */
async function fetchHomePopularMerged(
  api: AxiosInstance,
  fallbackFile: string,
  requestOrigin: string,
  requestEvent: H3Event | undefined,
): Promise<HomePopularNormalized> {
  const jsonPayload: HomePopularPayload | null = await loadFallbackJsonPayload<HomePopularPayload>(
    fallbackFile,
    DEFAULT_HOME_POPULAR_FALLBACK_FILE,
    requestOrigin,
  )

  let apiPayload: HomePopularPayload | null = null
  try {
    const { data } = await api.get<HomePopularPayload>('/api/home-popular', { timeout: 8000 })
    apiPayload = data ?? null
  } catch (error: unknown) {
    CachedPageUtils.reportFailedApiCall('/api/home-popular', error, requestEvent)
    apiPayload = null
  }

  const fromJson = mergeHomePopular(cloneHomePopular(EMPTY_HOME_POPULAR), jsonPayload)
  return mergeHomePopular(fromJson, apiPayload)
}

/**
 * Sections populaires de la home — JSON fallback (`public/`) puis patch API.
 * @returns {UseHomePopularDataReturn} `homePopular` calculé depuis JSON + patch API.
 */
export function useHomePopularData(): UseHomePopularDataReturn {
  const config: ReturnType<typeof useRuntimeConfig> = useRuntimeConfig()
  const api: AxiosInstance = useFrontApi()
  const homePopularFallbackFile: string = config.public.homePopularFallbackFile
  const requestOrigin: string = useRequestURL().origin
  const requestEvent: H3Event | undefined = useRequestEvent()

  const { data: homePopularMerged } = useAsyncData<HomePopularNormalized>(
    'home-popular',
    (): Promise<HomePopularNormalized> =>
      fetchHomePopularMerged(api, homePopularFallbackFile, requestOrigin, requestEvent),
    {
      server: true,
      lazy: false,
      default: () => cloneHomePopular(EMPTY_HOME_POPULAR),
      getCachedData(key: string, nuxtApp: NuxtApp): HomePopularNormalized | undefined {
        const cached: HomePopularNormalized | undefined = nuxtApp.payload.data[key] as
          | HomePopularNormalized
          | undefined
        if (
          import.meta.client &&
          cached &&
          cached.popularJobs?.length === 0 &&
          cached.popularSchools?.length === 0 &&
          cached.popularAdvice?.length === 0
        ) {
          return EMPTY_HOME_POPULAR
        }
        return cached
      },
    },
  )

  const homePopular: ComputedRef<HomePopularNormalized> = computed<HomePopularNormalized>(
    (): HomePopularNormalized => homePopularMerged.value ?? cloneHomePopular(EMPTY_HOME_POPULAR),
  )

  return { homePopular }
}
